import {useEffect, useRef, useState} from 'react';
import {MemoryRouter, Routes} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {enrollRoutes} from '@client/pages/public/enroll/enrollRoutes';
import {
  ENROLL_PREVIEW_CHANNEL,
  EnrollPreviewProvider,
  type EnrollPreviewMessage,
} from '@client/pages/public/enroll/enrollPreview';
import type {GroupConfig} from '@common/enrollmentSite/types';

function formatSavedAt(updatedAt: string | Date): string {
  const date = typeof updatedAt === 'string' ? new Date(updatedAt) : updatedAt;
  return date.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
}

// Renders the real member enroll flow from a site's SAVED draft in preview mode (config
// injected, gate skippable, submission stubbed). Mounted at the app root so its
// MemoryRouter isn't nested; auth comes from the internal `get` endpoint.
export function EnrollmentSitePreview({slug}: {slug: string}) {
  // Saved draft — refetches on window focus, so switching back after a save shows it.
  const {data, isLoading, isError} = trpc.internal.enrollmentSite.get.useQuery(
    {groupSlug: slug},
    {enabled: !!slug},
  );
  const utils = trpc.useUtils();
  const [refreshing, setRefreshing] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  // Live-reload: the builder pings this channel on save/publish. Focus-refetch covers
  // the separate-tab case; this covers a torn-off window that never loses visibility.
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const channel = new BroadcastChannel(ENROLL_PREVIEW_CHANNEL);
    channel.onmessage = (event: MessageEvent<EnrollPreviewMessage>) => {
      if (event.data?.type !== 'saved' || event.data.slug !== slug) return;
      setRefreshing(true);
      void utils.internal.enrollmentSite.get.invalidate({groupSlug: slug});
      // Hold the note a few seconds — the refetch itself is near-instant.
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setRefreshing(false), 3000);
    };
    return () => {
      channel.close();
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [slug, utils]);

  if (isLoading) {
    return (
      <div className='grid min-h-screen place-items-center bg-neutral-50'>
        <div className='h-7 w-7 animate-spin rounded-full border-[3px] border-neutral-200 border-t-neutral-500' />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className='grid min-h-screen place-items-center bg-neutral-50 p-6 text-center text-sm text-neutral-600'>
        Couldn’t load this site’s preview. It may not exist, or you may not have access.
      </div>
    );
  }

  const config = data.siteConfig as GroupConfig;

  return (
    <EnrollPreviewProvider
      value={{
        isPreview: true,
        config,
        savedAt: formatSavedAt(data.updatedAt),
        refreshing,
        siteOrigin: data.siteOrigin,
      }}
    >
      <MemoryRouter initialEntries={[`/${config.slug}`]}>
        <Routes>{enrollRoutes()}</Routes>
      </MemoryRouter>
    </EnrollPreviewProvider>
  );
}
